import { useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme, alpha } from '@mui/material/styles';

// MUI Icons for hazards
import WaterIcon from '@mui/icons-material/Water';
import ThunderstormIcon from '@mui/icons-material/Thunderstorm';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import GppBadIcon from '@mui/icons-material/GppBad';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import FloodIcon from '@mui/icons-material/Flood';

// MUI Icons for infrastructure
import OpacityIcon from '@mui/icons-material/Opacity';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import SchoolIcon from '@mui/icons-material/School';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

// Step indicator icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const isDevelopment = process.env.NODE_ENV === 'development';
const myBaseUrl = isDevelopment
  ? process.env.REACT_APP_API_BASE_URL_LOCAL
  : process.env.REACT_APP_API_BASE_URL_DEPLOY;

interface FormState {
  role: string;
  needs: string[];
  hazards: string[];
  infrastructure: string[];
  populationSize: string;
  firstName: string;
  lastName: string;
  organization: string;
  email: string;
  message: string;
}

const ROLES = [
  { value: 'urban_planner', label: 'Urban Planner' },
  { value: 'climate_strategist', label: 'Climate Adaptation Strategist' },
  { value: 'utility_company', label: 'Utility Company' },
  { value: 'emergency_manager', label: 'Emergency Manager' },
  { value: 'property_manager', label: 'Property / Asset Manager' },
  { value: 'researcher', label: 'Researcher / Academic' },
  { value: 'other', label: 'Other' },
];

const NEEDS = [
  { value: 'contingency', label: 'Contingency Planning' },
  { value: 'adaptation', label: 'Long-term Adaptation Strategy' },
  { value: 'early_warning', label: 'Early Warning System' },
  { value: 'risk_mapping', label: 'Hazard Risk Mapping' },
  { value: 'impact_assessment', label: 'Impact Assessment' },
  { value: 'resilience', label: 'Resilience Building' },
  { value: 'compliance', label: 'Climate Compliance Reporting' },
];

const HAZARDS = [
  { value: 'floods', label: 'Floods', icon: <FloodIcon fontSize="small" /> },
  { value: 'storms', label: 'Storms', icon: <ThunderstormIcon fontSize="small" /> },
  { value: 'droughts', label: 'Droughts', icon: <WbSunnyIcon fontSize="small" /> },
  { value: 'cyber', label: 'Cyber / Malicious Attacks', icon: <GppBadIcon fontSize="small" /> },
  { value: 'power_outage', label: 'Power Outages', icon: <PowerOffIcon fontSize="small" /> },
  { value: 'sea_level', label: 'Sea Level Rise', icon: <WaterIcon fontSize="small" /> },
  { value: 'other', label: 'Other', icon: <MoreHorizIcon fontSize="small" /> },
];

const INFRASTRUCTURE = [
  { value: 'water', label: 'Water', icon: <OpacityIcon fontSize="small" /> },
  { value: 'electricity', label: 'Electricity', icon: <ElectricBoltIcon fontSize="small" /> },
  { value: 'transport', label: 'Transport', icon: <DirectionsBusIcon fontSize="small" /> },
  { value: 'healthcare', label: 'Healthcare', icon: <LocalHospitalIcon fontSize="small" /> },
  { value: 'firefighting', label: 'Firefighting', icon: <LocalFireDepartmentIcon fontSize="small" /> },
  { value: 'police', label: 'Police', icon: <LocalPoliceIcon fontSize="small" /> },
  { value: 'education', label: 'Education', icon: <SchoolIcon fontSize="small" /> },
  { value: 'government', label: 'Government Services', icon: <AccountBalanceIcon fontSize="small" /> },
  { value: 'other', label: 'Other', icon: <MoreHorizIcon fontSize="small" /> },
];

const POPULATION_SIZES = [
  { value: 'small', label: '< 25,000', sublabel: 'Small municipality' },
  { value: 'medium', label: '25,000 – 150,000', sublabel: 'Mid-size municipality' },
  { value: 'large', label: '> 150,000', sublabel: 'Large city / region' },
];

const STEPS = ['Your Role', 'Your Needs', 'Key Hazards', 'Infrastructure', 'Municipality Size', 'Contact'];

interface SelectCardProps {
  label: string;
  sublabel?: string;
  icon?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
  accent: string;
}

const SelectCard = ({ label, sublabel, icon, selected, onClick, accent }: SelectCardProps) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: 'pointer',
      border: '1.5px solid',
      borderColor: selected ? accent : 'divider',
      borderRadius: 2,
      px: 2,
      py: 1.5,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      bgcolor: selected ? alpha(accent, 0.08) : 'background.paper',
      transition: 'all 0.18s ease',
      '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.05) },
    }}
  >
    {icon && <Box sx={{ color: selected ? accent : 'text.secondary', display: 'flex' }}>{icon}</Box>}
    <Box>
      <Typography variant="body2" fontWeight={selected ? 600 : 400}
        sx={{ color: selected ? accent : 'text.primary', lineHeight: 1.3 }}>
        {label}
      </Typography>
      {sublabel && <Typography variant="caption" color="text.secondary">{sublabel}</Typography>}
    </Box>
    {selected && <CheckCircleIcon sx={{ ml: 'auto', fontSize: 18, color: accent }} />}
  </Box>
);

const StepCard = ({ accent, children }: { accent: string; children: React.ReactNode }) => (
  <Box sx={{
    bgcolor: alpha(accent, 0.07),
    border: `1.5px solid ${alpha(accent, 0.2)}`,
    borderRadius: 3,
    p: { xs: 3, sm: 4 },
    mb: 4,
  }}>
    {children}
  </Box>
);

const Contact = (): JSX.Element => {
  const theme = useTheme();
  const accent = theme.palette.success.dark;

  const [step, setStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    role: '',
    needs: [],
    hazards: [],
    infrastructure: [],
    populationSize: '',
    firstName: '',
    lastName: '',
    organization: '',
    email: '',
    message: '',
  });

  const toggle = (field: 'needs' | 'hazards' | 'infrastructure', value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  const canAdvance = () => {
    if (step === 0) return !!form.role;
    if (step === 1) return form.needs.length > 0;
    if (step === 2) return form.hazards.length > 0;
    if (step === 3) return form.infrastructure.length > 0;
    if (step === 4) return !!form.populationSize;
    if (step === 5) return !!form.firstName && !!form.email;
    return false;
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(
        myBaseUrl + '/contact/enquiry/',
        {
          role: form.role,
          needs: form.needs,
          hazards: form.hazards,
          infrastructure: form.infrastructure,
          population_size: form.populationSize,
          first_name: form.firstName,
          last_name: form.lastName,
          organization: form.organization,
          email: form.email,
          message: form.message,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      setSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly at contact@citywisestrategy.com.');
    } finally {
      setLoading(false);
    }
  };

  const progress = (step / (STEPS.length - 1)) * 100;

  if (submitted) {
    return (
      <Box id="contact" sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container maxWidth="sm">
          <Box textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 64, color: accent, mb: 3 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Thank you, {form.firstName}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              We've received your enquiry and will be in touch within 1–2 business days.
              A confirmation email is on its way to <strong>{form.email}</strong>.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box id="contact" sx={{ py: 12, bgcolor: 'background.default' }}>
      <Container maxWidth="md">

        <Box textAlign="center" mb={6}>
          <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: 'text.primary' }}>
            See how CityWise fits your{' '}
            <Box component="span" sx={{ color: accent }}>municipality</Box>
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 540, mx: 'auto' }}>
            Tell us about your context and we'll show you exactly how CityWise can support your planning.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1, sm: 2 }, mb: 4, flexWrap: 'wrap' }}>
          {STEPS.map((label, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {i < step
                ? <CheckCircleIcon sx={{ fontSize: 18, color: accent }} />
                : <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: i === step ? accent : 'text.disabled' }} />
              }
              <Typography variant="caption" fontWeight={i === step ? 700 : 400}
                sx={{ color: i === step ? accent : i < step ? 'text.secondary' : 'text.disabled' }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        <LinearProgress variant="determinate" value={progress}
          sx={{
            mb: 6, height: 4, borderRadius: 2,
            bgcolor: alpha(accent, 0.12),
            '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 2 },
          }}
        />

        {/* Step 0: Role */}
        {step === 0 && (
          <StepCard accent={accent}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>What best describes your role?</Typography>
            <Grid container spacing={1.5} mt={1}>
              {ROLES.map((r) => (
                <Grid item xs={12} sm={6} key={r.value}>
                  <SelectCard label={r.label} selected={form.role === r.value}
                    onClick={() => setForm((p) => ({ ...p, role: r.value }))} accent={accent} />
                </Grid>
              ))}
            </Grid>
          </StepCard>
        )}

        {/* Step 1: Needs */}
        {step === 1 && (
          <StepCard accent={accent}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              What are your primary needs?{' '}
              <Typography component="span" variant="caption" color="text.secondary">(select all that apply)</Typography>
            </Typography>
            <Grid container spacing={1.5} mt={1}>
              {NEEDS.map((n) => (
                <Grid item xs={12} sm={6} key={n.value}>
                  <SelectCard label={n.label} selected={form.needs.includes(n.value)}
                    onClick={() => toggle('needs', n.value)} accent={accent} />
                </Grid>
              ))}
            </Grid>
          </StepCard>
        )}

        {/* Step 2: Hazards */}
        {step === 2 && (
          <StepCard accent={accent}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Which hazards are you most concerned about?{' '}
              <Typography component="span" variant="caption" color="text.secondary">(select all that apply)</Typography>
            </Typography>
            <Grid container spacing={1.5} mt={1}>
              {HAZARDS.map((h) => (
                <Grid item xs={12} sm={6} key={h.value}>
                  <SelectCard label={h.label} icon={h.icon} selected={form.hazards.includes(h.value)}
                    onClick={() => toggle('hazards', h.value)} accent={accent} />
                </Grid>
              ))}
            </Grid>
          </StepCard>
        )}

        {/* Step 3: Infrastructure */}
        {step === 3 && (
          <StepCard accent={accent}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Which infrastructure systems or services are you most concerned about?{' '}
              <Typography component="span" variant="caption" color="text.secondary">(select all that apply)</Typography>
            </Typography>
            <Grid container spacing={1.5} mt={1}>
              {INFRASTRUCTURE.map((inf) => (
                <Grid item xs={12} sm={6} key={inf.value}>
                  <SelectCard label={inf.label} icon={inf.icon} selected={form.infrastructure.includes(inf.value)}
                    onClick={() => toggle('infrastructure', inf.value)} accent={accent} />
                </Grid>
              ))}
            </Grid>
          </StepCard>
        )}

        {/* Step 4: Population size */}
        {step === 4 && (
          <StepCard accent={accent}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              What is the population size of your municipality or area of responsibility?
            </Typography>
            <Grid container spacing={2} mt={1}>
              {POPULATION_SIZES.map((p) => (
                <Grid item xs={12} sm={4} key={p.value}>
                  <SelectCard label={p.label} sublabel={p.sublabel} selected={form.populationSize === p.value}
                    onClick={() => setForm((prev) => ({ ...prev, populationSize: p.value }))} accent={accent} />
                </Grid>
              ))}
            </Grid>
          </StepCard>
        )}

        {/* Step 5: Contact details */}
        {step === 5 && (
          <Box>
            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name *" value={form.firstName} size="small"
                  onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name" value={form.lastName} size="small"
                  onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email address *" type="email" value={form.email} size="small"
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Organization or municipality" value={form.organization} size="small"
                  onChange={(e) => setForm((p) => ({ ...p, organization: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Your message to CityWise" value={form.message}
                  onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                  multiline rows={4} />
              </Grid>
            </Grid>

            <Box sx={{ bgcolor: alpha(accent, 0.05), border: `1px solid ${alpha(accent, 0.15)}`,
              borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Your selections summary:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {form.role && (
                  <Chip size="small" label={ROLES.find(r => r.value === form.role)?.label}
                    sx={{ bgcolor: alpha(accent, 0.1), color: accent, fontWeight: 600 }} />
                )}
                {form.needs.map(v => (
                  <Chip key={v} size="small" label={NEEDS.find(n => n.value === v)?.label} variant="outlined" />
                ))}
                {form.hazards.map(v => (
                  <Chip key={v} size="small" label={HAZARDS.find(h => h.value === v)?.label} variant="outlined" />
                ))}
                {form.infrastructure.map(v => (
                  <Chip key={v} size="small" label={INFRASTRUCTURE.find(i => i.value === v)?.label} variant="outlined" />
                ))}
                {form.populationSize && (
                  <Chip size="small" label={POPULATION_SIZES.find(p => p.value === form.populationSize)?.label} variant="outlined" />
                )}
              </Box>
            </Box>

            {error && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, color: 'error.main' }}>
                <ErrorOutlineIcon fontSize="small" />
                <Typography variant="body2">{error}</Typography>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary">
              By submitting this form you accept that we handle your information in accordance with our{' '}
              <a href="/privacy-policy" style={{ color: accent }}>Privacy Policy</a>.
            </Typography>
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />}
            onClick={() => setStep((s) => s - 1)} disabled={step === 0}
            sx={{ borderColor: 'divider', color: 'text.secondary', visibility: step === 0 ? 'hidden' : 'visible' }}>
            Back
          </Button>

          {step < STEPS.length - 1 ? (
            <Button variant="contained" endIcon={<ArrowForwardIcon />}
              onClick={() => setStep((s) => s + 1)} disabled={!canAdvance()}
              sx={{
                bgcolor: accent, px: 4,
                '&:hover': { bgcolor: theme.palette.success.main },
                '&:disabled': { bgcolor: alpha(accent, 0.3) },
              }}>
              Continue
            </Button>
          ) : (
            <Button variant="contained"
              endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={handleSubmit} disabled={!canAdvance() || loading}
              sx={{
                bgcolor: accent, px: 4,
                '&:hover': { bgcolor: theme.palette.success.main },
                '&:disabled': { bgcolor: alpha(accent, 0.3) },
              }}>
              {loading ? 'Sending...' : 'Send enquiry'}
            </Button>
          )}
        </Box>

      </Container>
    </Box>
  );
};

export default Contact;
